import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Home,
  Sun,
  Eye,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const LouvreSizesPage = () => {
  const louvreSizes = [
    {
      size: '63mm',
      name: 'Classic Balance',
      description: 'The most popular choice, offering the perfect balance between light control and privacy. Suitable for most window sizes and home styles.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/it53cftc_08.%20London%20Smoke%20Shutters%20-%20Tier%20on%20Tier%20Open.jpg',
      features: [
        'Perfect balance achieved',
        'Most popular choice',
        'Versatile for all windows',
        'Excellent light control',
        'Good privacy levels',
        'Universal appeal'
      ],
      lightControl: 'Balanced Control',
      privacy: '★★★★☆',
      lightTransmission: '★★★☆☆',
      modernLook: '★★★★☆',
      idealFor: ['Living rooms', 'Dining rooms', 'Kitchens', 'Most home styles'],
      color: 'text-gray-600',
      icon: Eye
    },
    {
      size: '76mm',
      name: 'Versatile Option',
      description: 'A versatile choice that works beautifully in both modern and traditional settings. Offers excellent light transmission while maintaining good privacy.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/25wu22aa_cafe%20style.jpeg',
      features: [
        'Versatile styling',
        'Modern and traditional fit',
        'Excellent light flow',
        'Good privacy balance',
        'Popular contemporary choice',
        'Wide appeal'
      ],
      lightControl: 'Popular Choice',
      privacy: '★★★☆☆',
      lightTransmission: '★★★★☆',
      modernLook: '★★★★☆',
      idealFor: ['Open plan living', 'Modern homes', 'Large windows', 'Bright spaces'],
      color: 'text-gray-800',
      icon: Sun
    },
    {
      size: '89mm',
      name: 'Contemporary Look',
      description: 'Perfect for modern homes seeking a contemporary aesthetic with maximum light transmission. Ideal for creating bright, airy spaces.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg',
      features: [
        'Contemporary aesthetic',
        'Maximum light transmission',
        'Modern home perfect',
        'Bright, airy feeling',
        'Sleek appearance',
        'Designer choice'
      ],
      lightControl: 'Bright & Airy',
      privacy: '★★★☆☆',
      lightTransmission: '★★★★☆',
      modernLook: '★★★★★',
      idealFor: ['Modern homes', 'Large spaces', 'South-facing rooms', 'Designer interiors'],
      color: 'text-gray-700',
      icon: Sun
    },
    {
      size: '114mm',
      name: 'Bold Statement',
      description: 'The ultimate choice for making a bold architectural statement. Perfect for large windows and spaces where maximum light and dramatic impact are desired.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Bold architectural statement',
        'Perfect for large windows',
        'Maximum light flow',
        'Dramatic visual impact',
        'Ultra-modern appeal',
        'Premium choice'
      ],
      lightControl: 'Maximum Light',
      privacy: '★★☆☆☆',
      lightTransmission: '★★★★★',
      modernLook: '★★★★★',
      idealFor: ['Large windows', 'Modern architecture', 'High ceilings', 'Statement spaces'],
      color: 'text-gray-600',
      icon: Maximize2
    }
  ];

  const comparisonData = [
    { feature: 'Privacy Level', size63: '★★★★☆', size76: '★★★☆☆', size89: '★★★☆☆', size114: '★★☆☆☆' },
    { feature: 'Light Transmission', size63: '★★★☆☆', size76: '★★★★☆', size89: '★★★★☆', size114: '★★★★★' },
    { feature: 'Modern Appeal', size63: '★★★★☆', size76: '★★★★☆', size89: '★★★★★', size114: '★★★★★' },
    { feature: 'Traditional Fit', size63: '★★★★★', size76: '★★★★☆', size89: '★★★☆☆', size114: '★★☆☆☆' },
    { feature: 'Versatility', size63: '★★★★★', size76: '★★★★★', size89: '★★★★☆', size114: '★★★☆☆' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Choose Your Perfect
              <span className="block text-slate-700">Louvre Size</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From classic 63mm for balanced control to bold 114mm for maximum light, 
              find the perfect louvre size to match your style and functional needs.
            </p>
          </div>

          {/* Size Overview */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {louvreSizes.map((louvre, index) => (
              <Card key={index} className="text-center border-2 hover:border-blue-300 transition-colors">
                <CardContent className="p-4">
                  <div className={`text-2xl font-bold ${louvre.color} mb-1`}>{louvre.size}</div>
                  <Badge variant="outline" className="text-xs">{louvre.lightControl}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Sections */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-20">
            {louvreSizes.map((louvre, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                {/* Image */}
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <div className="relative">
                    <img 
                      src={louvre.image} 
                      alt={`${louvre.size} louvre shutters`}
                      className="w-full h-96 object-cover rounded-2xl shadow-xl"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 rounded-full px-4 py-2">
                      <div className={`text-3xl font-bold ${louvre.color}`}>{louvre.size}</div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-3 mb-4">
                    <louvre.icon className={`h-8 w-8 ${louvre.color}`} />
                    <div>
                      <h2 className="text-4xl font-bold text-gray-900">{louvre.size}</h2>
                      <p className={`text-lg font-medium ${louvre.color}`}>{louvre.name}</p>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{louvre.description}</p>
                  
                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {louvre.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Performance Indicators */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Performance:</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl">{louvre.privacy}</div>
                        <div className="text-sm text-gray-600">Privacy</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl">{louvre.lightTransmission}</div>
                        <div className="text-sm text-gray-600">Light</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl">{louvre.modernLook}</div>
                        <div className="text-sm text-gray-600">Modern</div>
                      </div>
                    </div>
                  </div>

                  {/* Ideal For */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Ideal for:</h4>
                    <div className="flex flex-wrap gap-2">
                      {louvre.idealFor.map((room, i) => (
                        <Badge key={i} variant="outline" className={louvre.color}>{room}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Louvre Size Comparison</h2>
            <p className="text-xl text-gray-600">
              Compare all louvre sizes to find your perfect match
            </p>
          </div>

          <Card className="overflow-hidden shadow-xl">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-4 font-semibold">Feature</th>
                      <th className="text-center p-4 font-semibold text-gray-600">63mm</th>
                      <th className="text-center p-4 font-semibold" style={{color: '#8CB89F'}}>76mm</th>
                      <th className="text-center p-4 font-semibold text-gray-600">89mm</th>
                      <th className="text-center p-4 font-semibold text-gray-700">114mm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-4 font-medium">{row.feature}</td>
                        <td className="p-4 text-center">{row.size63}</td>
                        <td className="p-4 text-center">{row.size76}</td>
                        <td className="p-4 text-center">{row.size89}</td>
                        <td className="p-4 text-center">{row.size114}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Size Selection Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Quick Selection Guide</h2>
            <p className="text-xl text-gray-600">Not sure which size to choose? Here's our expert recommendation:</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2" style={{borderColor: '#8CB89F', backgroundColor: '#f0f9f5'}}>
              <CardHeader>
                <CardTitle style={{color: '#6b8f73'}}>For Privacy Focus</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  Choose <strong>63mm</strong> louvres for bedrooms, bathrooms, 
                  or street-facing windows where privacy is the primary concern.
                </p>
                <Badge style={{backgroundColor: '#8CB89F', color: 'white'}}>Recommended: 63mm</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-300 bg-gray-50">
              <CardHeader>
                <CardTitle className="text-gray-700">For Light & Style</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  Choose <strong>76mm, 89mm, or 114mm</strong> louvres for living areas, 
                  kitchens, or modern homes where light and contemporary style matter most.
                </p>
                <Badge className="bg-gray-600 text-white">Recommended: 89mm</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Choose Your Louvre Size?</h2>
          <p className="text-xl mb-8 text-white opacity-90">
            Get expert advice on the perfect louvre size for your windows and receive a personalized quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 text-lg">
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg"
            >
              <Home className="mr-2 h-5 w-5" />
              Book Consultation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LouvreSizesPage;